import React, { useEffect, useRef } from 'react';
import { Platform, ScrollView, View } from 'react-native';

import {
  Button,
  IconClose,
  IconDirections,
  IconTelephone,
  NotFound,
  Pressable,
  Text,
  iosVerticalScrollContentContainerStyle,
  tokens,
} from '@fhs/ui';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';

import { StoreDetailsSkeleton } from './store-details.skeleton';
import { useStoreDetailsMqStyles } from './store-details.styles';
import type { StoreDetailsViewProps } from './types';

const isWeb = Platform.OS === 'web';

/** The design writes the meridiem as "AM" / "PM". The shared time format emits "a.m." / "p.m.". */
function uppercaseMeridiem(time: string): string {
  return time.replace(/a\.m\./gi, 'AM').replace(/p\.m\./gi, 'PM');
}

export const StoreDetailsView: React.VFC<StoreDetailsViewProps> = ({
  addressLines,
  badge,
  ctaAccessibilityLabel,
  ctaLabel,
  hoursRows,
  hoursText,
  isCtaDisabled,
  isCtaLoading,
  isLoading,
  name,
  shouldFocusOnOpen,
  onCallPress,
  onClose,
  onCtaPress,
  onDirectionsPress,
  services,
  statusText,
}) => {
  const styles = useStoreDetailsMqStyles();
  const isDesktop = useIsDesktopBp();
  const closeButtonRef = useRef<View>(null);

  const actionButtonSize = isDesktop ? 'md' : 'sm';
  const actionIconSize = 16;

  // The panel takes the list's place rather than opening a route, so nothing else moves focus
  // off the store list for us.
  useEffect(() => {
    if (!isWeb || !shouldFocusOnOpen) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const element = closeButtonRef.current as unknown as { focus?: () => void } | null;
      element?.focus?.();
    });

    return () => cancelAnimationFrame(frameId);
  }, [shouldFocusOnOpen]);

  const closeButton = (
    <Pressable
      ref={closeButtonRef}
      testID="store-details-close-button"
      accessibilityLabel="Close"
      dd-action-name="store details close"
      onPress={onClose}
      borderRadius={999}
      style={styles.closeButton}
      hoveredStyle={styles.closeButtonHovered}
    >
      <IconClose size={isDesktop ? 32 : 24} color={tokens.colors.$houseDark} />
    </Pressable>
  );

  if (isLoading) {
    return (
      <View style={styles.root}>
        {closeButton}
        <StoreDetailsSkeleton />
      </View>
    );
  }

  if (!name) {
    return <NotFound />;
  }

  return (
    <View style={styles.root}>
      {closeButton}

      {/* iOS bounces a vertical scroll view even when its content fits, which lets the details
          be dragged around inside the sheet. */}
      <ScrollView
        bounces={false}
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, iosVerticalScrollContentContainerStyle]}
      >
        <View style={styles.summary}>
          {badge ? (
            <View
              style={[
                styles.badge,
                badge.tone === 'error' ? styles.badgeError : styles.badgeWarning,
              ]}
            >
              <Text.Ui
                size="sm"
                weight="bold"
                style={badge.tone === 'error' ? styles.badgeErrorText : styles.badgeWarningText}
              >
                {badge.label}
              </Text.Ui>
            </View>
          ) : null}

          {statusText ? (
            <Text.Ui size="xs" weight="semibold">
              {hoursText ? `${statusText} • ` : statusText}
              {hoursText ? <Text.Ui size="xs">{uppercaseMeridiem(hoursText)}</Text.Ui> : null}
            </Text.Ui>
          ) : null}

          <Text.Heading type="one" style={styles.storeName}>
            {name}
          </Text.Heading>

          {addressLines.length ? (
            // One text node, like the design: the street and city share a line break, with no
            // gap of their own on top of the summary's spacing.
            <Text.Ui size="md">{addressLines.join('\n')}</Text.Ui>
          ) : null}
        </View>

        {onCallPress || onDirectionsPress ? (
          <View style={styles.actionRow}>
            {onCallPress ? (
              <Button
                size={actionButtonSize}
                type="outline"
                style={styles.actionButton}
                dd-action-name={`${name} call restaurant`}
                onPress={onCallPress}
              >
                <Button.Icon size={actionIconSize}>
                  <IconTelephone />
                </Button.Icon>
                <Button.Text>Call Restaurant</Button.Text>
              </Button>
            ) : null}

            {onDirectionsPress ? (
              <Button
                size={actionButtonSize}
                style={styles.actionButton}
                dd-action-name={`${name} get directions`}
                onPress={onDirectionsPress}
              >
                <Button.Icon size={actionIconSize}>
                  <IconDirections />
                </Button.Icon>
                <Button.Text>Get Directions</Button.Text>
              </Button>
            ) : null}
          </View>
        ) : null}

        {services.length ? (
          <View style={styles.serviceRow}>
            {services.map(({ key, label, Icon }) => (
              <View key={key} style={styles.serviceChip}>
                <Icon
                  size={14}
                  color={tokens.colors.$houseDark}
                  accessibilityElementsHidden
                  importantForAccessibility="no-hide-descendants"
                  aria-hidden
                />
                <Text.Ui size="sm" weight="semibold">
                  {label}
                </Text.Ui>
              </View>
            ))}
          </View>
        ) : null}

        {hoursRows.length ? (
          <View style={styles.hours}>
            <Text.Heading type="two">Store Hours</Text.Heading>
            {hoursRows.map(({ day, label, intervalText }) => {
              const displayedInterval = uppercaseMeridiem(intervalText);

              return (
                <View
                  key={day}
                  style={styles.hoursRow}
                  accessible={!isWeb}
                  accessibilityLabel={`${label}, ${displayedInterval}`}
                >
                  <Text.Ui size="md">{label}</Text.Ui>
                  <Text.Ui size="md">{displayedInterval}</Text.Ui>
                </View>
              );
            })}
          </View>
        ) : null}
      </ScrollView>

      {ctaLabel ? (
        <View style={styles.footer}>
          <Button
            size="xl"
            testID="store-details-order-here"
            dd-action-name={`${name} ${ctaLabel}`}
            accessibilityLabel={ctaAccessibilityLabel}
            disabled={isCtaDisabled || isCtaLoading}
            loading={isCtaLoading}
            onPress={onCtaPress}
          >
            <Button.Text>{ctaLabel}</Button.Text>
          </Button>
        </View>
      ) : null}
    </View>
  );
};
